import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TrainerLayout from '../../components/trainer/TrainerLayout';
import { Megaphone, Send, Clock, Users, X, Plus } from 'lucide-react';
import Button from '../../components/ui/Button';

const mockAnnouncements = [
  { id: 1, title: 'Week 7 Session Rescheduled', message: 'The Week 7 live session has been moved to Sunday 10 AM due to a bank holiday.', course: 'AI for Finance', sentAt: '2026-05-20', recipients: 342, readBy: 280 },
  { id: 2, title: 'New Assignment: Credit Risk Model', message: 'Please submit your credit risk model by Friday. Include your dataset and evaluation metrics.', course: 'AI for Finance', sentAt: '2026-05-18', recipients: 342, readBy: 310 },
  { id: 3, title: 'Python Environment Setup', message: 'Make sure you have Python 3.10+ and Jupyter installed before Week 4.', course: 'Data Science for Banking', sentAt: '2026-05-15', recipients: 128, readBy: 120 },
];

export default function TrainerAnnouncements(): React.JSX.Element {
  const [announcements, setAnnouncements] = useState(mockAnnouncements);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', course: 'AI for Finance' });

  const handleSend = () => {
    if (!form.title || !form.message) return;
    setAnnouncements(prev => [{
      id: Date.now(),
      title: form.title,
      message: form.message,
      course: form.course,
      sentAt: new Date().toISOString().split('T')[0],
      recipients: 342,
      readBy: 0,
    }, ...prev]);
    setForm({ title: '', message: '', course: 'AI for Finance' });
    setShowForm(false);
  };

  return (
    <TrainerLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Announcements</h1>
            <p className="text-sm text-slate-500">Send updates to your students</p>
          </div>
          <Button onClick={() => setShowForm(true)} leftIcon={<Plus className="w-4 h-4" />}>
            New Announcement
          </Button>
        </div>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Send Announcement</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Course</label>
                <select
                  value={form.course}
                  onChange={e => setForm({ ...form, course: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
                >
                  <option>AI for Finance</option>
                  <option>Data Science for Banking</option>
                  <option>Python for FP&A</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Announcement title"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Message</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Write your announcement..."
                  rows={4}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 resize-none"
                />
              </div>
              <Button onClick={handleSend} leftIcon={<Send className="w-4 h-4" />}>
                Send Announcement
              </Button>
            </div>
          </motion.div>
        )}

        <div className="space-y-4">
          {announcements.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-bold">{a.course}</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="w-3 h-3" /> {a.sentAt}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1">{a.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{a.message}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {a.recipients} recipients
                    </span>
                    <span className="flex items-center gap-1">
                      <Megaphone className="w-3 h-3" /> {a.readBy} read
                    </span>
                    <span className="text-amber-500 font-medium">
                      {Math.round((a.readBy / a.recipients) * 100)}% open rate
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </TrainerLayout>
  );
}
